/**
 * indexationLabels — les mots de l'écran pour la préparation des documents
 * (C19 « Indexer les documents », spec § 0.5, § 5.4 ; spec C26 § 10).
 *
 * Un document est « interrogeable », « en cours », « en attente », « illisible »
 * — jamais « indexé », ni « chunk », ni « vecteur », ni « embedding ». Module
 * pur, partagé par la fenêtre du dossier, le lecteur du bureau et le mobile.
 */

export type IndexationStatus = 'pending' | 'processing' | 'ready' | 'unreadable' | 'failed';

export interface IndexationInfo {
  status: IndexationStatus;
  /** Libellé humain de l'échec, tel que le serveur l'écrit. */
  reason?: string;
  chunksTotal?: number;
  chunksDone?: number;
  /** « En cours » sans tranche depuis un moment (lot abandonné : onglet fermé, téléphone en veille) : se relance. */
  interrompue?: boolean;
}

/** Ton de la mention, rendu par un token sémantique (`--ag-success`, `--ag-info`, `--ag-text-3`, `--ag-error`, `--ag-warning`). */
export type IndexationTon = 'succes' | 'info' | 'neutre' | 'erreur' | 'avertissement';

export interface ProgressionLot {
  applied: number;
  errors: number;
}

const s = (n: number): string => (n > 1 ? 's' : '');

/** Mention sous le nom d'un fichier. Absent = document jamais demandé. */
export function libelleIndexation(info: IndexationInfo | null | undefined): string {
  if (!info) return 'Pas encore interrogeable';
  switch (info.status) {
    case 'ready': return 'Interrogeable';
    case 'processing': return info.interrompue ? 'Interrompue' : 'En cours';
    case 'pending': return 'En attente';
    case 'unreadable': return `Illisible : ${info.reason ? premiereMinuscule(info.reason) : "ce document n'a pas de texte"}`;
    case 'failed': return `Préparation impossible : ${info.reason ? premiereMinuscule(info.reason) : 'réessayez plus tard'}`;
  }
}

export function tonIndexation(info: IndexationInfo | null | undefined): IndexationTon {
  if (!info) return 'neutre';
  switch (info.status) {
    case 'ready': return 'succes';
    case 'processing': return info.interrompue ? 'avertissement' : 'info';
    case 'pending': return 'neutre';
    case 'unreadable': return 'erreur';
    case 'failed': return 'avertissement';
  }
}

/** Sur la carte d'un document mobile : rien quand il est interrogeable (ou jamais demandé). */
export function libelleEtatMobile(info: IndexationInfo | null | undefined): string {
  if (!info) return '';
  switch (info.status) {
    case 'ready': return '';
    case 'processing': return info.interrompue ? 'interrompue' : 'en cours';
    case 'pending': return 'en attente';
    case 'unreadable': return 'illisible';
    case 'failed': return 'préparation impossible';
  }
}

/** Un document est-il interrogeable par l'assistant ? */
export function estInterrogeable(info: IndexationInfo | null | undefined): boolean {
  return info?.status === 'ready';
}

/** Peut-on lancer (ou relancer) sa préparation ? Jamais quand elle avance ou que le document n'a pas de texte ; oui quand elle est interrompue. */
export function peutPreparer(info: IndexationInfo | null | undefined): boolean {
  return !info || info.status === 'pending' || info.status === 'failed' || (info.status === 'processing' && info.interrompue === true);
}

/** « Préparation des documents : 3 sur 12 » pendant un lot. */
export function libelleLot(progress: ProgressionLot, resolvedCount: number): string {
  return `Préparation des documents : ${progress.applied + progress.errors} sur ${resolvedCount}`;
}

/**
 * « 12 documents interrogeables », « 10 interrogeables, 2 non préparés », « 1 document interrogeable » ;
 * un lot arrêté avant la fin (plafond du jour, service saturé) compte ce qui est resté en attente :
 * « 0 interrogeable, 1 en attente », « 6 interrogeables, 1 non préparé, 3 en attente ».
 */
export function libelleFinDeLot(progress: ProgressionLot, resolvedCount: number): string {
  const restants = Math.max(0, resolvedCount - progress.applied - progress.errors);
  if (progress.errors === 0 && restants === 0) {
    return `${resolvedCount} document${s(resolvedCount)} interrogeable${s(resolvedCount)}`;
  }
  const parts = [`${progress.applied} interrogeable${s(progress.applied)}`];
  if (progress.errors > 0) parts.push(`${progress.errors} non préparé${s(progress.errors)}`);
  if (restants > 0) parts.push(`${restants} en attente`);
  return parts.join(', ');
}

/** Bouton de la fenêtre du dossier. */
export function libelleBoutonPreparer(n: number): string {
  return `Rendre interrogeable${s(n)} (${n})`;
}

/** Le bouton du lecteur : ce qu'il dit et ce qu'il fait selon l'état du document. */
export type ActionDemander = 'demander' | 'preparer' | 'aucune';

export const DEMANDER_LABELS = {
  demander: 'Demander sur ce document',
  preparer: "Préparer ce document pour l'assistant",
  enCours: 'Document en cours de préparation',
  illisible: "Ce document est illisible : il n'a pas de texte",
  preparationLancee: 'Préparation en cours… l\'assistant s\'ouvrira quand le document sera interrogeable.',
  preparationImpossible: (raison: string) => `Préparation impossible : ${premiereMinuscule(raison)}`,
  /** Puce de portée dans l'assistant du bureau. */
  portee: (titre: string) => `Sur « ${titre} »`,
  retirerPortee: 'Retirer la portée du document',
  retirerPorteeOrganisation: 'Retirer la portée de l’organisation',
  /** La portée « organisation » de l'assistant personnel (C26 SP4, spec § 6). */
  porteeOrganisation: (nom: string) => `Avec les sources de ${nom}`,
  aucunePortee: 'Aucune portée',
  porteeBouton: 'Portée',
  porteeSansEcriture: 'L’écriture ne porte pas sur une organisation.',
  plusMembre: 'Vous ne faites plus partie de cette organisation.',
  /** Bascule de la sheet « Demander » (mobile). */
  cePassage: 'Ce passage',
  toutLeDocument: 'Tout le document',
  /** Ligne sous une réponse obtenue avec la portée d'un document. */
  dApresLeDocument: (titre: string) => `D'après le document · ${titre}`,
} as const;

export function actionDemander(info: IndexationInfo | null | undefined): { action: ActionDemander; libelle: string; desactive: boolean } {
  if (estInterrogeable(info)) return { action: 'demander', libelle: DEMANDER_LABELS.demander, desactive: false };
  if (info?.status === 'processing' && !info.interrompue) return { action: 'aucune', libelle: DEMANDER_LABELS.enCours, desactive: true };
  if (info?.status === 'unreadable') return { action: 'aucune', libelle: DEMANDER_LABELS.illisible, desactive: true };
  return { action: 'preparer', libelle: DEMANDER_LABELS.preparer, desactive: false };
}

function premiereMinuscule(texte: string): string {
  return texte.length > 0 ? texte[0].toLowerCase() + texte.slice(1) : texte;
}
